import type { ReactElement, ReactNode } from 'react';
import React, { useMemo } from 'react';

import type { INamedContainerProps } from '../models';

export type TNamedContainers<T extends string = string> = {
    [K in T]: ReactNode;
};

type TNamedContainersWithChildren<T extends string = string> = TNamedContainers<T> & {
    children: ReactNode[];
};

export function isNamedContainer(node: ReactNode): node is ReactElement<INamedContainerProps> {
    return Boolean(
        typeof node === 'object' &&
            node !== null &&
            (node as ReactElement).props.is === 'NamedContainer' &&
            (node as ReactElement).props.name
    );
}

/**
 * Функция собирает из children слоты и оставляет отфильтрованный список детей
 * @param children
 */
export function getNamedContainersWithChildren<T extends string>(children: ReactNode) {
    return React.Children.toArray(children).reduce<TNamedContainersWithChildren<T>>(
        (nodes, node) => {
            if (!isNamedContainer(node)) {
                nodes.children.push(node);
            } else if (node.props.name !== 'children') {
                return {
                    ...nodes,
                    [node.props.name]: node.props.children,
                };
            }

            return nodes;
        },
        { children: [] } as TNamedContainersWithChildren<T>
    );
}

/**
 * Функция собирает из children только слоты
 * @param children
 */
export function getNamedContainers<T extends string = string>(
    children: ReactNode
): TNamedContainers<T> {
    return React.Children.toArray(children).reduce<TNamedContainers>((nodes, node) => {
        if (isNamedContainer(node)) {
            return {
                ...nodes,
                [node.props.name]: node.props.children,
            };
        }

        return nodes;
    }, {});
}

export function useNamedContainersWithChildren<T extends string = string>(children: ReactNode) {
    return useMemo(() => getNamedContainersWithChildren<T>(children), [children]);
}

export function useNamedContainers<T extends string>(children: ReactNode) {
    return useMemo(() => getNamedContainers<T>(children), [children]);
}
